import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { TranslationKeys } from '../../i18n/types.js';
import type { UseMultiplayerReturn } from '../../hooks/useMultiplayer.js';

/**
 * One create command from Studio, awaiting its outcome.
 *
 * Every attempt carries its own `requestId` (payload field). The server echoes
 * it back to the sender in `command:error` and to GMs in the created-echo
 * (`journal:created` / `page:created` / `beat:created`), so an outcome is
 * matched to exactly one attempt — never by name or arrival order. A
 * same-named object created by another GM tab or the AI carries no / another
 * requestId and is ignored. `beat:update` is one step of a multi-step create
 * (link a beat's prep text): its refusal comes back with the requestId, its
 * success is matched on the echo's beat and value.
 */
export interface StudioCreateRequest {
  /** Client command type, e.g. 'journal:create'. */
  readonly command:
    | 'journal:create' | 'sheet:create' | 'page:create' | 'beat:create' | 'beat:update' | 'beat:add-material' | 'beat:remove-material'
    | 'beat:set-current' | 'beat:set-status' | 'page:activate'
    | 'beat:add-map' | 'beat:remove-map' | 'beat:set-primary-map' | 'beat:move-map'
    | 'map:set' | 'journal:set-owner' | 'page:set-owner';
  /** Server echo type that carries the created object. */
  readonly echo:
    | 'journal:created' | 'sheet:created' | 'page:created' | 'beat:created' | 'beat:updated' | 'beat:material-added' | 'beat:material-removed'
    | 'beat:current-changed' | 'beat:status-changed' | 'page:activated' | 'beat:maps-changed'
    | 'map:changed' | 'journal:owner-changed' | 'page:owner-changed';
  /** User-facing name, for the failure message. */
  readonly name: string;
  /** Failure message title instead of «Could not create “name”». */
  readonly failTitle?: string;
  /** Payload without id/timestamp/requestId — sent as-is on every attempt. */
  readonly payload: Record<string, unknown>;
  /**
   * Created id when `echo` is the result of the attempt `requestId`, else
   * undefined. Journal/page: the echo's requestId; sheet: the id Studio sent.
   */
  readonly matches: (echo: Record<string, unknown>, requestId: string) => string | undefined;
  readonly onCreated: (id: string) => void;
  /**
   * The attempt certainly did not create anything: refused by the server, or
   * not sent (disconnected). Only then is «Retry» offered.
   */
  readonly onFailed?: (reason: string, final?: boolean) => void;
  /** command:error codes that a retry would get again (a conflict, not a hiccup): reported without «Retry». */
  readonly finalCodes?: readonly string[];
  /**
   * After a lost answer the server could not say what the request did (no
   * record: evicted, reloaded, restarted). The object may exist: no «Retry» —
   * the GM checks and decides.
   */
  readonly onUnresolved?: (reason: string) => void;
  /**
   * The answer was lost with the socket: the outcome is unknown. With
   * `recover` it is resolved after the rejoin (onCreated / onFailed follow);
   * without, it stays unknown. Never a plain «Retry» — that could duplicate.
   */
  readonly onUnknown?: (reason: string) => void;
  /**
   * How a lost answer is recovered after the rejoin: 'ask' — the server
   * remembers what this requestId created (request:outcome), so the outcome is
   * asked for, never created again; 'resend' — the command is idempotent (an
   * update), it is sent again with the same requestId.
   */
  readonly recover?: 'ask' | 'resend';
  /** False once the owner no longer wants this request (the GM changed their mind): it is not resumed. */
  readonly alive?: () => boolean;
  /**
   * What «Retry» runs instead of re-sending the same request — a multi-step
   * create resumes from its unfinished step. Same guards: one use, this
   * Studio mount and this campaign only.
   */
  readonly retry?: () => void;
}

type Translate = (key: keyof TranslationKeys) => string;

/** Same text the table shows for a command:error (useMultiplayerSync). */
function commandErrorText(msg: Record<string, unknown>, t: Translate): string {
  const raw = typeof msg.error === 'string' ? msg.error : '';
  const code = typeof msg.code === 'string' ? msg.code : '';
  const errorKey = code !== '' ? `error.${code}` : '';
  const translated = errorKey !== '' ? t(errorKey as keyof TranslationKeys) : raw;
  return translated !== '' && translated !== errorKey ? translated : raw;
}

/**
 * Studio's command outcome channel. AppShell (whose router toasts every
 * command:error) is unmounted while Studio is open, so Studio surfaces the
 * errors itself: a correlated create gets a message with «Retry», any other
 * rejected command (reveal, pin, beat edits, …) a plain error toast. One
 * listener for both, so no error is shown twice.
 *
 * «Retry» belongs to this Studio mount and this campaign: one use; void after
 * Studio closes or the campaign changes (its toast is dismissed as well) —
 * `mp.send` writes to whatever socket is current, a stale toast must not
 * create the object in another campaign. A reconnect within the same
 * campaign keeps it valid: the refused object does not exist, retry creates
 * it once.
 *
 * `quiet` (the game plan at the table, package 4): no toasts at all — the
 * table's router already toasts every command:error, and the caller shows
 * the refusal next to the action (onFailed / onUnknown).
 */
export function useStudioCommands(
  mp: Pick<UseMultiplayerReturn, 'send' | 'onMessage' | 'status'> & {
    readonly session?: { readonly id: string } | null;
  },
  t: Translate,
  options: { readonly quiet?: boolean } = {},
): { create: (request: StudioCreateRequest) => void } {
  const quiet = options.quiet === true;
  const pendingRef = useRef(new Map<string, StudioCreateRequest>());
  // Answer lost with the socket, to be recovered after the rejoin (recover set).
  const unknownRef = useRef(new Map<string, StudioCreateRequest>());
  // request:outcome asked, answer awaited.
  const askingRef = useRef(new Map<string, StudioCreateRequest>());
  const campaignId: string | undefined = mp.session?.id;
  const campaignRef = useRef(campaignId);
  campaignRef.current = campaignId;
  const aliveRef = useRef(true);
  // Open «Retry» actions: token → toast id. An action runs only while its token is here.
  const retriesRef = useRef(new Map<number, string | number | undefined>());
  const nextTokenRef = useRef(0);
  const createRef = useRef<(request: StudioCreateRequest) => void>(() => {});

  const dropRetries = useCallback((): void => {
    for (const toastId of retriesRef.current.values()) {
      if (toastId !== undefined) toast.dismiss(toastId);
    }
    retriesRef.current.clear();
  }, []);

  const fail = useCallback(
    (request: StudioCreateRequest, reason: string, retryable = true): void => {
      if (retryable) request.onFailed?.(reason);
      else request.onFailed?.(reason, true);
      if (quiet) return;
      const title = request.failTitle ?? t('studio.create.failed').replace('{name}', request.name);
      if (!retryable) {
        toast.error(reason !== '' ? `${title}: ${reason}` : title);
        return;
      }
      const token = ++nextTokenRef.current;
      const ownerCampaign = campaignRef.current;
      const toastId = toast.error(reason !== '' ? `${title}: ${reason}` : title, {
        action: {
          label: t('studio.create.retry'),
          onClick: (): void => {
            if (!retriesRef.current.delete(token)) return;
            if (!aliveRef.current || campaignRef.current !== ownerCampaign) return;
            if (request.retry !== undefined) request.retry();
            else createRef.current(request);
          },
        },
      });
      retriesRef.current.set(token, toastId);
    },
    [t, quiet],
  );

  const create = useCallback(
    (request: StudioCreateRequest): void => {
      if (!aliveRef.current) return;
      // mp.send silently drops while disconnected — say so instead of waiting
      // for an outcome that will never come.
      if (mp.status !== 'connected') {
        fail(request, t('session.connectionLost'));
        return;
      }
      const requestId = crypto.randomUUID();
      pendingRef.current.set(requestId, request);
      mp.send({ type: request.command, ...request.payload, requestId } as never);
    },
    [mp, t, fail],
  );
  createRef.current = create;

  useEffect(() => {
    return mp.onMessage((incoming): void => {
      const msg = incoming as unknown as { type: string } & Record<string, unknown>;
      if (msg.type === 'command:error') {
        const requestId = typeof msg.requestId === 'string' ? msg.requestId : undefined;
        const failed = requestId !== undefined ? pendingRef.current.get(requestId) : undefined;
        if (failed !== undefined && requestId !== undefined) {
          pendingRef.current.delete(requestId);
          const final = typeof msg.code === 'string' && (failed.finalCodes ?? []).includes(msg.code);
          fail(failed, commandErrorText(msg, t), !final);
          return;
        }
        // The outcome question itself was refused: still unknown, asked again after the next rejoin.
        const asked = requestId !== undefined ? askingRef.current.get(requestId) : undefined;
        if (asked !== undefined && requestId !== undefined) {
          askingRef.current.delete(requestId);
          unknownRef.current.set(requestId, asked);
          asked.onUnknown?.(commandErrorText(msg, t));
          return;
        }
        if (!quiet) toast.error(commandErrorText(msg, t));
        return;
      }
      if (msg.type === 'request:outcome') {
        const requestId = typeof msg.requestId === 'string' ? msg.requestId : '';
        const asked = askingRef.current.get(requestId);
        if (asked === undefined) return;
        askingRef.current.delete(requestId);
        const createdId = typeof msg.createdId === 'string' ? msg.createdId : undefined;
        if (msg.outcome === 'created' && createdId !== undefined) {
          asked.onCreated(createdId);
        } else if (msg.outcome === 'gone') {
          fail(asked, t('studio.create.goneSince'), false);
        } else {
          // No record on the server proves nothing: the object may exist. Say
          // so and offer no Retry — a fresh create could duplicate it.
          asked.onUnresolved?.(t('studio.create.unresolved'));
          if (!quiet) toast.error(t('studio.create.unresolvedTitle').replace('{name}', asked.name));
        }
        return;
      }
      for (const [requestId, request] of pendingRef.current) {
        if (request.echo !== msg.type) continue;
        const id = request.matches(msg, requestId);
        if (id === undefined) continue;
        pendingRef.current.delete(requestId);
        request.onCreated(id);
        return;
      }
    });
  }, [mp, t, fail, quiet]);

  // A request in flight when the socket drops has an unknown outcome — the
  // server may have applied it. Its late echo (on the old socket) never comes.
  // A request that can be recovered waits for the rejoin; any other is
  // forgotten (if applied, the rejoin snapshot brings the object). None is
  // reported as failed: a «Retry» here could create a duplicate.
  // «connected» comes with session:joined — the rejoin snapshot is applied;
  // then the unknown ones are asked about (creates) or re-sent (updates).
  useEffect(() => {
    const send = mp.send;
    if (mp.status !== 'connected') {
      const dropped = [...pendingRef.current.entries(), ...askingRef.current.entries()];
      pendingRef.current.clear();
      askingRef.current.clear();
      for (const [requestId, request] of dropped) {
        if (request.recover !== undefined) unknownRef.current.set(requestId, request);
        request.onUnknown?.(t('session.connectionLost'));
      }
      return;
    }
    const unknown = [...unknownRef.current.entries()];
    unknownRef.current.clear();
    for (const [requestId, request] of unknown) {
      if (request.alive?.() === false) continue;
      if (request.recover === 'resend') {
        pendingRef.current.set(requestId, request);
        send({ type: request.command, ...request.payload, requestId } as never);
      } else {
        askingRef.current.set(requestId, request);
        send({ type: 'request:outcome', requestId } as never);
      }
    }
    // mp.send is stable per socket; only the status transition drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mp.status, t]);

  // Another campaign: nothing of the previous one may be retried or awaited.
  useEffect(() => {
    return (): void => {
      pendingRef.current.clear();
      unknownRef.current.clear();
      askingRef.current.clear();
      dropRetries();
    };
  }, [campaignId, dropRetries]);

  useEffect(() => {
    aliveRef.current = true;
    return (): void => {
      aliveRef.current = false;
    };
  }, []);

  return { create };
}
